import type { ReactElement, ReactNode } from "react";
import type { ZodObject, ZodRawShape } from "zod";
import { z } from "@/zod";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DetailFieldSectionContext } from "./context/DetailFieldSectionContext";
import {
  useDetailField,
  type UseDetailFieldOptions,
} from "./hooks/useDetailField";
import { cn } from "@/lib/utils";

interface SectionProps<S extends z.ZodTypeAny = z.ZodTypeAny> {
  schema: S;
  data: z.infer<S>;
  module: string;
  children: ReactNode;
  className?: string;
  /** `"default"` uses `detail-section-grid` (detail pages). `"card"` uses a responsive card grid. */
  variant?: "default" | "card";
}

type SectionFieldProps = UseDetailFieldOptions & {
  isVisible?: boolean;
  tooltip?: string;
};

interface SectionColumnProps {
  children: ReactNode;
  className?: string;
}

const SectionColumn = ({ children, className }: SectionColumnProps) => (
  <div className={cn("detail-column", className)}>{children}</div>
);

const SectionField = ({
  isVisible = true,
  tooltip,
  ...fieldOptions
}: SectionFieldProps) => {
  const { label, value } = useDetailField(fieldOptions);

  if (!isVisible) return null;

  const labelContent =
    typeof label === "string" || typeof label === "number"
      ? `${label}:`
      : label;

  return (
    <div className="detail-field-root">
      <span className="detail-field-label">{labelContent}</span>
      {tooltip ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="detail-field-value cursor-help">{value}</div>
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <div className="detail-field-value">{value}</div>
      )}
    </div>
  );
};

function SectionRoot<S extends z.ZodTypeAny>({
  schema,
  data,
  module,
  children,
  className,
  variant = "default",
}: SectionProps<S>) {
  const gridClass =
    variant === "card"
      ? "grid w-full min-w-0 grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-3"
      : "detail-section-grid";

  return (
    <DetailFieldSectionContext.Provider
      value={{
        schema: schema as unknown as ZodObject<ZodRawShape>,
        data: data as Record<string, unknown>,
        module,
      }}
    >
      <div className={cn(gridClass, className)}>{children}</div>
    </DetailFieldSectionContext.Provider>
  );
}

type SectionCompound = (<S extends z.ZodTypeAny>(
  props: SectionProps<S>,
) => ReactElement) & {
  Field: typeof SectionField;
  Column: typeof SectionColumn;
};

export const Section = Object.assign(SectionRoot, {
  Field: SectionField,
  Column: SectionColumn,
}) as SectionCompound;
